// src/components/Assets/hooks/useAssetExpenses.js
// Custom hook for Asset Expenses management - Phase 2 implementation

import { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../../UserContext";
import toast from "react-hot-toast";
import * as assetsApi from "../assetsApi";

/**
 * Custom hook for managing asset expenses
 */
export const useAssetExpenses = () => {
  const [t] = useTranslation();
  const { token, userData } = useContext(UserContext);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Extract tenantId from nested userData structure
  const tenantId = userData?.Tenant?.id || userData?.User?.tenantId || userData?.Employee?.tenantId;
  const farmId = userData?.Employee?.farmId;

  /**
   * Fetch all asset expenses
   */
  const fetchExpenses = useCallback(async (abortSignal) => {
    if (!tenantId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await assetsApi.fetchAssetExpenses(tenantId, farmId || null, token, abortSignal);

      // Sort by date descending (newest first)
      // Backend returns nested structure with payment object
      const sorted = data.sort((a, b) => {
        const aPayment = a.payment || a;
        const bPayment = b.payment || b;
        return new Date(bPayment.paymentDate) - new Date(aPayment.paymentDate);
      });

      setExpenses(sorted);
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        toast.error(t("Failed to load expenses"));
        console.error("Error fetching expenses:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId, farmId, token, t]);

  /**
   * Add a new asset expense
   */
  const addExpense = async (expenseData) => {
    console.log("🔍 Adding expense with data:", expenseData);

    if (!expenseData.assetId) {
      toast.error(t("Asset ID is required"));
      console.error("Missing assetId in expenseData");
      return false;
    }

    try {
      // Backend expects PaymentRequest format with specific field names
      const requestPayload = {
        ...expenseData,
        tenantId,
        farmId,
        type: "purchase"  // Android uses "purchase" for expenses (TransactionType.EXPENSE.type)
      };

      console.log("🔍 Sending to API:", requestPayload);

      const response = await assetsApi.createAssetExpense(requestPayload, token);

      // Check if backend returned an error in the response body (HTTP 200 but body.status >= 400)
      if (response.status && response.status >= 400) {
        toast.error(t(response.message) || t("Failed to add expense"));
        console.error("Error in response body:", response);
        return false;
      }

      toast.success(t(response.message) || t("Expense added successfully"));
      await fetchExpenses();
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || t("Failed to add expense");
      toast.error(t(errorMessage));
      console.error("Error adding expense:", err);
      console.error("Error response:", err.response?.data);
      return false;
    }
  };

  /**
   * Filter expenses by asset ID
   */
  const filterByAsset = (assetId) => {
    if (!assetId) return expenses;
    return expenses.filter(expense => {
      const payment = expense.payment || expense;
      return payment.assetId === assetId;
    });
  };

  /**
   * Filter expenses by date range
   */
  const filterByDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return expenses;

    return expenses.filter(expense => {
      const payment = expense.payment || expense;
      const expenseDate = new Date(payment.paymentDate);
      const start = startDate ? new Date(startDate) : new Date(0);
      const end = endDate ? new Date(endDate) : new Date();

      return expenseDate >= start && expenseDate <= end;
    });
  };

  /**
   * Calculate total expenses
   */
  const calculateTotal = (filteredExpenses = expenses) => {
    return filteredExpenses.reduce((total, expense) => {
      const payment = expense.payment || expense;
      return total + (payment.amountPaid || 0);
    }, 0);
  };

  // Fetch expenses on mount and when dependencies change
  useEffect(() => {
    const abortController = new AbortController();
    fetchExpenses(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchExpenses]);

  return {
    expenses,
    loading,
    error,
    fetchExpenses,
    addExpense,
    filterByAsset,
    filterByDateRange,
    calculateTotal
  };
};

export default useAssetExpenses;
